import type { AllPropertiesAssigned } from '@disy/lib/type-util';

import type { AttributeDto } from 'cadenza/api-client/dto/attribute-dto';
import type { AttributeModelDto } from 'cadenza/api-client/dto/attribute-model-dto';
import type {
  MultiAttributeHierarchyDto,
  MultiAttributeHierarchyLevelDto,
} from 'cadenza/api-client/dto/multi-attribute-hierarchy-dto';
import type { ObjecttypeMemberDto } from 'cadenza/api-client/dto/objecttype-member-dto';
import type { AttributeName } from 'cadenza/model/attribute-name.gen';
import { objecttypeIdToString } from 'cadenza/repository/model/objecttype-id.gen';
import type { ObjecttypeName } from 'cadenza/repository/model/objecttype-name.gen';
import type { RepositoryName } from 'cadenza/repository/model/repository-name.gen';
import type { AttributePickerAttribute } from 'cadenza/ui/attribute-picker/attribute-picker';
import type { AttributeTileAttribute } from 'cadenza/ui/attribute-tile/attribute-tile-attribute';
import { AttributeModificationKey } from 'cadenza/workbook/api/attribute-modification-key';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import { createAttributeId } from 'cadenza/workbook/model/attribute-id-util';

import i18n from './attribute-util.properties';

const APPEARS_UNMODIFIED: AttributeModificationKey[] = [
  'LOCAL_HIERARCHY_LEVEL',
  'USE_LOCAL_HIERARCHY_LEVEL',
  'USE_SCALE_DEPENDENT_HIERARCHY_LEVEL',
  'SORT_HIERARCHY_LEVEL',
  'SORT_ORDER',
  'VISIBLE',
];

// This file is meant for generic attribute utilities, which are independent of workbooks
// and thus can be used not only in a workbook page, but for example also in the management center.
// To have access to the workbook redux store and types, please use workbook-attribute-util.

// this method is effectively only used in workbooks, but cannot be moved to workbook-attribute-util
// because it's also used by the attribute-tile, which in turn is used in the management
// center. A movement would therefore (rightfully) cause unwanted dependencies.
export function isModified(attribute: AttributeTileAttribute) {
  const settings = attribute.settings;
  return (
    settings != null &&
    AttributeModificationKey.values.some((mod) => {
      // Only consider modifications, which can be set in the modifications dialog.
      return !APPEARS_UNMODIFIED.includes(mod) && mod in settings;
    })
  );
}

function isWorkbookAttribute(attribute: ObjecttypeMemberDto | WorkbookAttributeDto): attribute is WorkbookAttributeDto {
  return 'id' in attribute;
}

/** Converts attributes from various sources into `AttributePickerAttribute`s. */
export function createPickerAttributes(member: ObjecttypeMemberDto | WorkbookAttributeDto): AttributePickerAttribute[] {
  if (isWorkbookAttribute(member)) {
    return [createPickerAttributeFromWorkbookAttribute(member)];
  } else if (member.attribute) {
    return [createPickerAttributeFromAttribute(member.attribute)];
  } else if (member.multiAttributeHierarchy) {
    return createPickerAttributesFromHierarchy(member.multiAttributeHierarchy);
  }
  // this function does currently not support date hierarchies
  return [];
}

export function createPickerAttributeFromWorkbookAttribute(member: WorkbookAttributeDto) {
  // use RequireProperties to ensure that all AttributePickerAttribute properties are set to a sensible value
  // it is only assigned to the local variable to not pollute the outside world
  return {
    id: member.baseId ?? member.id,
    printName: member.printName,
    status: member.status,
    type: member.type,
    description: member.description,
    objecttypeId: member.objecttypeId,
    category: member.category,
    objecttypePrintName: member.objecttypePrintName,
    fromCombinedObjecttype: member.fromCombinedObjecttype,
    measureAggregation: member.measureAggregation,
    levels: member.levels,
    selectedLevelName: member.selectedLevelName,
    simpleGeometryType: member.simpleGeometryType,
    relatedGeometryType: member.relatedGeometryType,
    hasGeometryRelation: member.hasGeometryRelation,
    visibleInDataBrowser: member.visibleInDataBrowser,
    enrichedObjecttypeId: member.enrichedObjecttypeId,
    enrichedObjecttypePrintName: member.enrichedObjecttypePrintName,
    hierarchyId: member.hierarchyId,
    hierarchyType: member.hierarchyType,
    objecttypeBoundToWorkbook: false,
    dataSourceFlavor: member.dataSourceFlavor,
    dataSourcePrintName: member.dataSourcePrintName,
    dataSourceType: member.dataSourceType,
    databaseTableColumn: undefined,
    lobMediaType: member.lobMediaType,
    objecttypeAttributeName: member.objecttypeAttributeName,
    projectKind: member.projectKind,
    projectPrintName: member.projectPrintName,
    settings: member.settings,
    visible: member.visible,
    localAttributeType: member.localAttributeType,
    selectable: true,
  } satisfies AllPropertiesAssigned<AttributePickerAttribute>;
}

/**
 * Converts an `AttributeDto` into a representation understood by the `AttributePicker`.
 *
 * This function does not support hierachies, because the attribute picker expects each level to be its own attribute.
 * No, using `attribute.levels` does not work.
 *
 * Use `createPickerAttributesFromHierarchy` to convert hierarchies
 * or use `createPickerAttributes` that tries to work with all attribute formats.
 */
export function createPickerAttributeFromAttribute(
  attribute: AttributeDto,
  { useNameAsId = false } = {},
): AttributePickerAttribute {
  return {
    id: useNameAsId ? attribute.name : createAttributeId(attribute.objecttypeId, attribute.name),
    objecttypeId: attribute.objecttypeId,
    printName: attribute.printName,
    status: 'OK',
    type: attribute.attributeType,
    lobMediaType: attribute.lobMediaType,
    category: attribute.dimension ? 'DIMENSION' : 'MEASURE',
    measureAggregation: attribute.aggregationFunction,
    objecttypePrintName: attribute.objecttypePrintName,
    description: attribute.description,
    databaseTableColumn: attribute.databaseTableColumn,
    fromCombinedObjecttype: attribute.fromCombinedObjecttype,
    simpleGeometryType: attribute.simpleGeometryType,
    hasGeometryRelation: attribute.relationGroupContainsGeometry,
    visibleInDataBrowser: attribute.visibleInDataBrowser,
  };
}

/**
 * Creates a representation understood by the `AttributePicker` from a name (as string).
 */
export function createAttributePickerAttributeFromString(name: string, missing: boolean): AttributePickerAttribute {
  return {
    category: 'DIMENSION',
    id: name as AttributeName,
    printName: missing ? i18n('attribute.unknown.name') : name,
    status: missing ? 'MISSING' : 'OK',
    type: 'STRING',
    objecttypeId: objecttypeIdToString('unknown' as RepositoryName, 'unknown' as ObjecttypeName),
    visibleInDataBrowser: true,
  };
}

/**
 * Converts an `MultiAttributeHierarchyDto` into a representation understood by the `AttributePicker`.
 *
 * This requires splitting the hierarchy into an `AttributePickerAttribute` for itsel as well as creating an
 * `AttributePickerAttribute` for each hierarchy level.
 */
function createPickerAttributesFromHierarchy(hierarchy: MultiAttributeHierarchyDto): AttributePickerAttribute[] {
  const hierarchyPickerAttribute: AttributePickerAttribute = {
    id: createAttributeId(hierarchy.objecttype.id, hierarchy.name),
    printName: hierarchy.printName,
    description: hierarchy.hierarchyLevels[0].attribute.description,
    type: hierarchy.hierarchyLevels[0].attribute.attributeType,
    status: hierarchy.hierarchyLevels[0].attribute.status!,
    category: 'HIERARCHY',
    objecttypeId: hierarchy.objecttype.id,
    hierarchyType: 'MULTI_ATTRIBUTE_HIERARCHY',
    objecttypePrintName: hierarchy.objecttype.printName,
    visibleInDataBrowser: hierarchy.visibleInDataBrowser,
  };
  const hierarchyLevelPickerAttributes = hierarchy.hierarchyLevels.map((level) =>
    createPickerAttributeFromHierarchyLevel(hierarchy, level),
  );
  return [hierarchyPickerAttribute, ...hierarchyLevelPickerAttributes];
}

/**
 * Converts a `MultiAttributeHierarchyLevelDto` into a representation understood by the `AttributePicker`.
 *
 * Setting `hierarchyId` is required to allow `renderAttributesAndHierarchies` to undo this transformation later.
 */
function createPickerAttributeFromHierarchyLevel(
  hierarchy: MultiAttributeHierarchyDto,
  hierarchyLevel: MultiAttributeHierarchyLevelDto,
): AttributePickerAttribute {
  return {
    id: createAttributeId(hierarchyLevel.attribute.objecttypeId, hierarchyLevel.attribute.name),
    hierarchyId: createAttributeId(hierarchy.objecttype.id, hierarchy.name),
    printName: hierarchyLevel.attribute.printName,
    description: hierarchyLevel.attribute.description,
    type: hierarchyLevel.attribute.attributeType,
    status: hierarchyLevel.attribute.status!,
    category: hierarchyLevel.attribute.category,
    objecttypeId: hierarchy.objecttype.id,
    objecttypePrintName: hierarchy.objecttype.printName,
    visibleInDataBrowser: hierarchy.visibleInDataBrowser,
  };
}

export function createAttributeModelDtoFromAttribute(attribute: AttributeDto): AttributeModelDto {
  const attributePickerAttribute = createPickerAttributeFromAttribute(attribute);
  return Object.assign(attributePickerAttribute, {
    name: attribute.name,
    format: attribute.format,
    durationUnit: attribute.durationUnit,
    attributeColorSettings: attribute.attributeColorSettings,
    objecttypeId: attribute.objecttypeId,
  });
}

export function createAttributeModelDtoFromWorkbookAttribute(attribute: WorkbookAttributeDto): AttributeModelDto {
  const attributePickerAttribute = createPickerAttributeFromWorkbookAttribute(attribute);
  return Object.assign(attributePickerAttribute, {
    id: attribute.id,
    name: attribute.objecttypeAttributeName!,
    format: attribute.format,
    durationUnit: attribute.durationUnit,
    attributeColorSettings: attribute.attributeColorSettings,
    visibleInDataBrowser: attribute.visibleInDataBrowser,
  });
}
